import {
  Alert,
  Button,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { saveContacts } from "../service/CompanyDocumentsService";
import { useCompanyDocumentsLanguage } from "../i18n/CompanyDocumentsLanguageContext";

const empty = () => ({
  name: "",
  email: "",
  phone: "",
});

export default function OnboardingContacts({
  section,
  contacts,
  onSaved,
}) {
  const { t } =
    useCompanyDocumentsLanguage();

  const [rows, setRows] = useState(
    contacts.length
      ? contacts.map(
          ({ name, email, phone }) => ({
            name,
            email,
            phone,
          }),
        )
      : [empty()],
  );

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (section !== "C") {
    return (
      <Alert severity="error">
        {t(
          "client.contacts.invalidSection",
        )}
      </Alert>
    );
  }

  const change = (index, field, value) =>
    setRows((current) =>
      current.map((row, rowIndex) =>
        rowIndex === index
          ? {
              ...row,
              [field]: value,
            }
          : row,
      ),
    );

  const remove = (index) =>
    setRows((current) =>
      current.filter(
        (_, rowIndex) => rowIndex !== index,
      ),
    );

  const save = async (event) => {
    event.preventDefault();

    setBusy(true);
    setError("");

    try {
      await saveContacts("C", rows);
      await onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={save}
      className="grid gap-3 border-t pt-5"
    >
      <div>
        <h3 className="font-semibold">
          {t("client.contacts.title")}
        </h3>

        <p className="mt-1 text-sm text-[#5F737F]">
          {t(
            "client.contacts.description",
          )}
        </p>
      </div>

      {error && (
        <Alert severity="error">{error}</Alert>
      )}

      {rows.map((row, index) => (
        <div
          key={index}
          className="grid gap-3 rounded-lg border p-3 md:grid-cols-[1fr_1fr_1fr_auto]"
        >
          <TextField
            label={t(
              "client.contacts.name",
              { index: index + 1 },
            )}
            size="small"
            required
            value={row.name}
            disabled={busy}
            inputProps={{ maxLength: 150 }}
            onChange={(event) =>
              change(
                index,
                "name",
                event.target.value,
              )
            }
          />

          <TextField
            label={t(
              "client.contacts.email",
              { index: index + 1 },
            )}
            size="small"
            type="email"
            required
            value={row.email}
            disabled={busy}
            inputProps={{ maxLength: 254 }}
            onChange={(event) =>
              change(
                index,
                "email",
                event.target.value,
              )
            }
          />

          <TextField
            label={t(
              "client.contacts.phone",
              { index: index + 1 },
            )}
            size="small"
            required
            value={row.phone}
            disabled={busy}
            inputProps={{ maxLength: 20 }}
            placeholder="+62..."
            onChange={(event) =>
              change(
                index,
                "phone",
                event.target.value,
              )
            }
          />

          <Button
            type="button"
            disabled={busy || rows.length === 1}
            onClick={() => remove(index)}
          >
            {t("client.contacts.remove")}
          </Button>
        </div>
      ))}

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outlined"
          disabled={busy || rows.length >= 20}
          onClick={() =>
            setRows((current) => [
              ...current,
              empty(),
            ])
          }
        >
          {t("client.contacts.add")}
        </Button>

        <Button
          type="submit"
          variant="contained"
          disabled={busy}
        >
          {t("client.contacts.save")}
        </Button>
      </div>
    </form>
  );
}
